// Workspace keyboard shortcuts, the shortcut reference and the sidebar search entry.
import { t } from "../core/i18n.js";
import { esc } from "../core/model.js";
import { goToday, navigate, setPage } from "../core/navigation.js";
import { runtime } from "../core/runtime.js";
import { openPlanModal } from "./plans.js";
import { pausePom, resetPom, startPom } from "./timers.js";
import { toggleCommandPalette } from "./command-palette.js";
import { modKey, undoLast } from "./undo.js";

const GO_TO = {
  d: "today",
  w: "weekly",
  t: "table",
  a: "all",
  e: "exams",
  p: "pomodoro",
  m: "manage",
  s: "settings",
};
const SEQUENCE_WINDOW = 1200;

export const shortcutKeys = {
  palette: [modKey(), "K"],
  help: ["?"],
  undo: [modKey(), "Z"],
  add: ["C"],
  today: ["T"],
  prev: ["←"],
  next: ["→"],
  timer: ["Space"],
  reset: ["R"],
  ...Object.fromEntries(
    Object.entries(GO_TO).map(([key, page]) => [
      "page:" + page,
      ["G", key.toUpperCase()],
    ]),
  ),
};

const sections = () => [
  [
    "General",
    [
      ["Open the command menu", shortcutKeys.palette],
      ["Keyboard shortcuts", shortcutKeys.help],
      ["Undo the last deletion", shortcutKeys.undo],
      ["Close a window", ["Esc"]],
    ],
  ],
  [
    "Plans and days",
    [
      ["Add plan", shortcutKeys.add],
      ["Go to today", shortcutKeys.today],
      ["Previous day", ["←", "P"]],
      ["Next day", ["→", "N"]],
    ],
  ],
  [
    "Go to",
    [
      ["Dashboard", shortcutKeys["page:today"]],
      ["Weekly", shortcutKeys["page:weekly"]],
      ["Plan Table", shortcutKeys["page:table"]],
      ["Pomodoro", shortcutKeys["page:pomodoro"]],
      ["Exam Dates", shortcutKeys["page:exams"]],
      ["Manage Plans", shortcutKeys["page:manage"]],
      ["Settings", shortcutKeys["page:settings"]],
    ],
  ],
  [
    "Focus timer",
    [
      ["Start or pause", shortcutKeys.timer],
      ["Reset focus timer", shortcutKeys.reset],
    ],
  ],
];

const chips = (keys) => keys.map((key) => `<kbd>${esc(key)}</kbd>`).join("");

export function openShortcutHelp() {
  const host = document.getElementById("modal");
  host.innerHTML = `<div class="modal shortcut-help"><div class="shortcut-help-head"><h2>${t("Keyboard shortcuts")}</h2><button class="iconbtn" type="button" data-close aria-label="${t("Close")}">✕</button></div><div class="shortcut-grid">${sections()
    .map(
      ([title, rows]) =>
        `<section><h3>${t(title)}</h3><dl>${rows.map(([label, keys]) => `<div><dt>${t(label)}</dt><dd>${chips(keys)}</dd></div>`).join("")}</dl></section>`,
    )
    .join(
      "",
    )}</div><p class="shortcut-help-foot">${t("Letter shortcuts pause while you type in a field.")}</p></div>`;
  host.classList.add("show");
}

export function renderSidebarSearch() {
  const host = document.getElementById("sidebarSearch");
  if (!host) return;
  host.innerHTML = `<button type="button" class="sidebar-search" data-command-palette><svg class="ui-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg><span>${t("Search")}</span><span class="sidebar-search-keys">${chips(shortcutKeys.palette)}</span></button>`;
}

export function bindShortcuts() {
  renderSidebarSearch();
  window.addEventListener("dayx:language", renderSidebarSearch);
  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-command-palette]")) return;
    runtime.menuOpen = false;
    document.body.classList.remove("menu-open");
    toggleCommandPalette();
  });
  let sequence = 0;
  document.addEventListener("keydown", (e) => {
    if (document.body.dataset.presentation !== "workspace") return;
    const key = e.key.toLowerCase(),
      mod = e.ctrlKey || e.metaKey;
    // The command menu opens from anywhere, including text fields.
    if (mod && !e.altKey && !e.shiftKey && key === "k") {
      const modal = document.getElementById("modal");
      if (
        modal.classList.contains("show") &&
        !modal.querySelector(".command-palette, .shortcut-help")
      )
        return;
      e.preventDefault();
      toggleCommandPalette();
      return;
    }
    if (
      e.defaultPrevented ||
      document.getElementById("modal").classList.contains("show")
    )
      return;
    const typing =
      e.target.matches("input,textarea,select") || e.target.isContentEditable;
    if (mod && !e.altKey && !e.shiftKey && key === "z") {
      if (!typing && undoLast()) e.preventDefault();
      return;
    }
    if (typing || mod || e.altKey) return;
    if (e.target.closest("[role=menu],[role=listbox]")) return;
    if (sequence && Date.now() - sequence < SEQUENCE_WINDOW) {
      sequence = 0;
      if (GO_TO[key]) {
        e.preventDefault();
        setPage(GO_TO[key]);
      }
      return;
    }
    sequence = 0;
    if (e.key === "?") {
      e.preventDefault();
      openShortcutHelp();
      return;
    }
    if (key === "g") sequence = Date.now();
    else if (key === "c") {
      e.preventDefault();
      openPlanModal();
    } else if (key === "n") navigate(1);
    else if (key === "p") navigate(-1);
    else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      // Arrows only move days where the date is visible.
      if (["today", "weekly", "all"].includes(runtime.page))
        navigate(e.key === "ArrowRight" ? 1 : -1);
    } else if (key === "t") goToday();
    else if (key === "r") resetPom();
    else if (e.code === "Space") {
      if (e.target.closest("button, a, [role=button]")) return;
      e.preventDefault();
      runtime.focusTimer?.running ? pausePom() : startPom();
    }
  });
}
